/**
 * Binary blob handling for in-app recordings (and later: uploads/attachments).
 *
 * Local-first: the CID is computed locally (UnixFS DAG, same pattern as
 * ingest-server's createUnixFS/uploadUnixFS), the bytes are kept in OPFS,
 * and the CAR upload to the wovin store happens opportunistically — failed
 * uploads land in a localStorage-backed pending queue for retry.
 */

import { CarWriter } from '@ipld/car'
import { Logger } from 'besonders-logger'
import { MemoryBlockstore } from 'blockstore-core/memory'
import { importer } from 'ipfs-unixfs-importer'
import { CID } from 'multiformats/cid'
import { storageState } from '../ipfs/storage'

const { LOG, WARN, DEBUG } = Logger.setup(Logger.WARN, { prefix: '[blob-upload]' })

const OPFS_DIR = 'audio-recordings'
const PENDING_KEY = 'note3.audio.pendingUploads'

export interface UnixFSResult {
	rootCid: CID
	blocks: Map<string, Uint8Array> // CID string -> block bytes
	size: number
}

/**
 * Create a UnixFS DAG from file content — the rootCid IS the blob/cid,
 * computable fully offline. (Default chunker: fixed 256KB chunks.)
 */
export async function createUnixFS(content: Uint8Array, filename?: string): Promise<UnixFSResult> {
	const blockstore = new MemoryBlockstore()
	const source = [{ path: filename || 'file', content }]

	let rootCid: CID | undefined
	let size = 0
	for await (
		const entry of importer(source, blockstore, {
			cidVersion: 1,
			rawLeaves: true,
		})
	) {
		DEBUG('UnixFS entry:', entry.path, entry.cid.toString())
		rootCid = entry.cid
		size = Number(entry.size)
	}
	if (!rootCid) throw new Error('Failed to create UnixFS DAG')

	const blocks = new Map<string, Uint8Array>()
	for await (const { cid, block } of blockstore.getAll()) {
		blocks.set(cid.toString(), block)
	}
	return { rootCid, blocks, size }
}

/** Bundle a UnixFS DAG into a CAR blob for `storeCar` upload. */
export async function carFromUnixFS(unixfs: UnixFSResult): Promise<Blob> {
	const { writer, out } = CarWriter.create([unixfs.rootCid])

	const chunks: Uint8Array[] = []
	const collectChunks = (async () => {
		for await (const chunk of out) chunks.push(chunk)
	})()

	for (const [cidStr, block] of unixfs.blocks) {
		await writer.put({ cid: CID.parse(cidStr), bytes: block })
	}
	await writer.close()
	await collectChunks

	return new Blob(chunks.map(c => c as BlobPart), { type: 'application/vnd.ipld.car' })
}

async function getOpfsDir() {
	const root = await navigator.storage.getDirectory()
	return await root.getDirectoryHandle(OPFS_DIR, { create: true })
}

/** Persist recording bytes locally (OPFS), keyed by CID. */
export async function saveBlobToOpfs(cidStr: string, blob: Blob): Promise<void> {
	const dir = await getOpfsDir()
	const fileHandle = await dir.getFileHandle(cidStr, { create: true })
	const writable = await fileHandle.createWritable()
	await writable.write(blob)
	await writable.close()
}

/** Read locally persisted recording bytes, or null if absent. */
export async function readBlobFromOpfs(cidStr: string): Promise<Blob | null> {
	try {
		const dir = await getOpfsDir()
		const fileHandle = await dir.getFileHandle(cidStr)
		return await fileHandle.getFile()
	} catch {
		return null
	}
}

interface PendingUpload {
	cid: string
	mimeType: string
}

function getPendingUploads(): PendingUpload[] {
	try {
		return JSON.parse(localStorage.getItem(PENDING_KEY) || '[]')
	} catch {
		return []
	}
}
function setPendingUploads(pending: PendingUpload[]) {
	localStorage.setItem(PENDING_KEY, JSON.stringify(pending))
}

async function tryUploadCar(car: Blob): Promise<boolean> {
	const providers = storageState.ucanUploads
	if (!providers?.length) {
		WARN('No upload providers initialized (yet?)')
		return false
	}
	const results = await Promise.allSettled(providers.map(p => p.storeCar(car)))
	const ok = results.some(r => r.status === 'fulfilled')
	if (!ok) WARN('All CAR uploads failed:', results)
	return ok
}

/**
 * Upload a blob's UnixFS CAR to the wovin store; on failure (offline, no
 * providers) queue it for retry — the bytes stay available in OPFS.
 * Returns whether the upload succeeded now.
 */
export async function uploadBlob(unixfs: UnixFSResult, mimeType: string): Promise<boolean> {
	const cidStr = unixfs.rootCid.toString()
	const car = await carFromUnixFS(unixfs)
	const ok = await tryUploadCar(car).catch((e) => {
		WARN('CAR upload threw:', e)
		return false
	})
	if (ok) {
		LOG('Uploaded recording CAR', cidStr)
		setPendingUploads(getPendingUploads().filter(p => p.cid !== cidStr))
	} else {
		LOG('Queued recording for later upload', cidStr)
		const pending = getPendingUploads()
		if (!pending.some(p => p.cid === cidStr)) {
			setPendingUploads([...pending, { cid: cidStr, mimeType }])
		}
	}
	return ok
}

/**
 * Retry uploads that previously failed (bytes come from OPFS).
 * TODO: wire into sync-service online/interval handling; currently invoked
 * lazily when a new recording finishes.
 */
export async function retryPendingBlobUploads(): Promise<void> {
	const pending = getPendingUploads()
	if (!pending.length) return
	DEBUG(`Retrying ${pending.length} pending blob uploads`)
	for (const entry of pending) {
		const blob = await readBlobFromOpfs(entry.cid)
		if (!blob) {
			WARN('Pending upload lost its OPFS bytes, dropping:', entry.cid)
			setPendingUploads(getPendingUploads().filter(p => p.cid !== entry.cid))
			continue
		}
		const unixfs = await createUnixFS(new Uint8Array(await blob.arrayBuffer()))
		if (unixfs.rootCid.toString() !== entry.cid) {
			WARN('CID mismatch on retry (corrupt OPFS bytes?), dropping:', entry.cid)
			setPendingUploads(getPendingUploads().filter(p => p.cid !== entry.cid))
			continue
		}
		await uploadBlob(unixfs, entry.mimeType)
	}
}
